import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import './HellFire.css';

const vert = `
  void main() {
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const frag = `
  #define NUM_OCTAVES 4
  uniform vec2 resolution;
  uniform vec3 color1;
  uniform vec3 color0;
  uniform float time;

  float rand(vec2 n) {
    return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
  }

  float noise(vec2 p) {
    vec2 ip = floor(p);
    vec2 u = fract(p);
    u = u * u * (3.0 - 2.0 * u);
    float res = mix(
      mix(rand(ip), rand(ip + vec2(1.0, 0.0)), u.x),
      mix(rand(ip + vec2(0.0, 1.0)), rand(ip + vec2(1.0, 1.0)), u.x),
      u.y
    );
    return res * res;
  }

  float fbm(vec2 x) {
    float v = 0.0;
    float a = 0.5;
    vec2 shift = vec2(100);
    mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
    for (int i = 0; i < NUM_OCTAVES; ++i) {
      v += a * noise(x);
      x = rot * x * 2.0 + shift;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec2 newUv = uv + vec2(0.0, -time * 0.0004);
    float scale = 10.0;
    vec2 p = newUv * scale;
    float n = fbm(p + fbm(p));

    float intensity = 1.0 - uv.y;

    /* Near the top of the canvas, switch to higher-frequency noise so
       flame tips become narrow and pointed rather than a broad smooth
       band. tipBlend = 0 at bottom, 1 at the very top.              */
    float tipBlend = pow(clamp((uv.y - 0.5) / 0.5, 0.0, 1.0), 2.0);
    vec2 tipP = (uv + vec2(0.0, -time * 0.0004)) * 22.0;
    float tipN = fbm(tipP + fbm(tipP));
    float blendedN = mix(n, tipN, tipBlend);

    /* Minimum noise weight so peaks can break through even at the top.
       At top (intensity=0): fireVal = blendedN*0.55 — only sharpest
       high-freq spikes exceed the 0.52 threshold → jagged flame tips. */
    float fireVal = intensity + blendedN * (intensity * 0.45 + 0.55);

    /* Threshold: below this = fully transparent */
    if (fireVal < 0.52) {
      gl_FragColor = vec4(0.0);
      return;
    }

    /* Map fireVal range [0.52 .. ~1.8] to a gradient:
       0.52–0.70 → deep red (outer edge / tips)
       0.70–1.00 → orange
       1.00–1.30 → bright amber/yellow
       1.30+     → near-white core */
    float t = clamp((fireVal - 0.52) / 1.0, 0.0, 1.0);

    vec3 deepRed  = vec3(0.55, 0.04, 0.00);
    vec3 orange   = vec3(1.00, 0.38, 0.02);
    vec3 amber    = vec3(1.00, 0.72, 0.10);
    vec3 white    = vec3(1.00, 0.95, 0.80);

    vec3 col;
    if (t < 0.33) {
      col = mix(deepRed, orange, t / 0.33);
    } else if (t < 0.66) {
      col = mix(orange, amber, (t - 0.33) / 0.33);
    } else {
      col = mix(amber, white, (t - 0.66) / 0.34);
    }

    gl_FragColor = vec4(col, 1.0);
  }
`;

export default function HellFire() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setPixelRatio(1); // cap at 1 — retina renders 4× pixels for a blurry shader
    renderer.setSize(width * 0.75, height * 0.75); // 75% res — tips need enough pixels to look jagged
    renderer.setClearColor(0x000000, 0); // fully transparent background
    mount.appendChild(renderer.domElement);


    // Full-screen quad — vertex shader maps directly to NDC so no camera needed
    const uniforms = {
      time:       { value: 10.0 },
      resolution: { value: new THREE.Vector2(width, height) },
      color1:     { value: new THREE.Vector3(255, 110, 10) },  // bright orange flame body
      color0:     { value: new THREE.Vector3(200, 30, 0) },    // dark red outer edge
    };

    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: vert,
      fragmentShader: frag,
      transparent: true,
    });
    scene.add(new THREE.Mesh(geometry, material));

    let animId;

    // Pause when off-screen
    let visible = true;
    const observer = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0 });
    observer.observe(mount);

    /* Punch an even-odd hole in the canvas so fire glows around the
       Hellhound card but doesn't paint over its face (same visual as
       stone tablets that share a stacking context with the fire).    */
    function updateClipPath() {
      const card = document.querySelector('.lesson-card--beast');
      if (!card) return;
      const mRect = mount.getBoundingClientRect();
      const cRect = card.getBoundingClientRect();
      const W = Math.round(mRect.width);
      const H = Math.round(mRect.height);
      // card coords relative to this canvas; may extend above/below canvas bounds
      const t = Math.round(cRect.top    - mRect.top);
      const b = Math.round(cRect.bottom - mRect.top);
      const l = Math.round(cRect.left   - mRect.left);
      const r = Math.round(cRect.right  - mRect.left);
      // outer rect (fills canvas) + inner rect (hole) — evenodd rule subtracts the hole
      const d = `M0,0 H${W} V${H} H0 Z M${l},${t} H${r} V${b} H${l} Z`;
      mount.style.clipPath = `path(evenodd, '${d}')`;
    }
    // Run after first paint so layout is settled
    setTimeout(updateClipPath, 50);

    // Resize
    function onResize() {
      const w = mount.clientWidth, h = mount.clientHeight;
      renderer.setSize(w * 0.75, h * 0.75);
      material.uniforms.resolution.value.set(w * 0.75, h * 0.75);
      updateClipPath();
    }
    window.addEventListener('resize', onResize);

    // Wrap animate to skip frames when hidden
    cancelAnimationFrame(animId);
    function animateVisible(delta) {
      animId = requestAnimationFrame(animateVisible);
      if (!visible) return;
      material.uniforms.time.value = delta;
      renderer.render(scene, camera);
    }
    animateVisible(0);

    return () => {
      cancelAnimationFrame(animId);
      observer.disconnect();
      window.removeEventListener('resize', onResize);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="hellfire" aria-hidden="true" />;
}
